import ProtectedComponent from './protected-component.js';
import { renderSafetyArmadillo } from '../utils/mascots.js';
import { isFeatureEnabled } from '../utils/feature-flags.js';
import { renderEmojiBadge, renderNavLabel, renderPageTitle } from '../utils/emoji-badge.js';
import {
  subscribeContextNavStack,
  getContextNavLayers,
  popTopLayer,
  clearContextNav
} from '../utils/context-nav-stack.js';
import { appendReturnTo } from '../utils/return-to-url.js';


// Taco Menu Web Component
class TacoMenu extends ProtectedComponent {
  constructor() {
    super();
    this._syncContextNavBound = this.syncContextNavDom.bind(this);
    this._unsubContextNav = null;
    this.isOpen = false;
    // Defer reading window.innerWidth to avoid forced reflow on construction
    // Will be set properly in setupResponsiveHandling
    this.isWideScreen = false;
    this.currentUser = null;
    this.friendRequestCount = 0;
    this.pendingInvitationCount = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachEventListeners();
    this.setupResponsiveHandling();
    this._unsubContextNav = subscribeContextNavStack(() => this.syncContextNavDom());
    document.addEventListener('route-changed', this._syncContextNavBound);
    document.addEventListener('section-shown', this._syncContextNavBound);
    this.handleFeatureFlagsLoaded = () => this.updateFeatureNavVisibility();
    document.addEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
    this.syncContextNavDom();
    this.updateActiveMenuItem();
  }

  disconnectedCallback() {
    if (this._unsubContextNav) {
      this._unsubContextNav();
      this._unsubContextNav = null;
    }
    document.removeEventListener('route-changed', this._syncContextNavBound);
    document.removeEventListener('section-shown', this._syncContextNavBound);
    if (this.handleFeatureFlagsLoaded) {
      document.removeEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
    }
    super.disconnectedCallback();
  }

  render() {
    // Start hidden until auth status is confirmed
    this.style.display = 'none';

    this.innerHTML = `
      <div class="taco-menu-container">
        <!-- Slide-in Menu -->
        <nav class="slide-menu ${this.isWideScreen ? 'always-open' : ''}" id="slideMenu">
          <div class="menu-header">
            ${renderPageTitle('🌮', 'Menu')}
            <button class="close-menu-btn" id="closeMenuBtn">&times;</button>
          </div>
          
          <div class="menu-content">
            <div id="contextNavMount" class="context-nav-mount" hidden></div>

            <!-- Main Navigation (hidden while context nav stack has layers) -->
            <div class="menu-section primary-site-nav" id="primarySiteNav">
              <h3>Navigation</h3>
              <ul class="menu-items">
                <li><a href="/" class="menu-item" data-route="/">${renderNavLabel('🏠', 'Home')}</a></li>
                <li><a href="/profile/me" class="menu-item" data-route="/profile/me">${renderNavLabel('👤', 'My Profile')}</a></li>
                <li><a href="/friends" class="menu-item" data-route="/friends">
                  ${renderNavLabel('🎪', 'Friends')}
                  <span class="notification-badge" id="friendsBadge" style="display: none;">0</span>
                </a></li>
                <li><a href="/events" class="menu-item" data-route="/events">${renderNavLabel('📅', 'Events')}</a></li>
                <li id="groupsNavItem"><a href="/groups" class="menu-item" data-route="/groups">${renderNavLabel('🌱', 'Groups')}</a></li>
                <li id="mascotParadeNavItem" style="display: none;"><a href="/mascot-parade" class="menu-item" data-route="/mascot-parade">${renderNavLabel('🎪', 'Mascot Parade')}</a></li>
                <li><a href="/memories" class="menu-item" data-route="/memories">${renderNavLabel('🕰️', 'Memories')}</a></li>
                <li><a href="/settings" class="menu-item" data-route="/settings">${renderNavLabel('⚙️', 'Settings')}</a></li>
                <li><a href="/docs" class="menu-item" data-route="/docs">${renderNavLabel('📖', 'Docs')}</a></li>
              </ul>
            </div>

            <!-- Admin Section -->
            <div class="menu-section admin-section" id="adminSection" style="display: none;">
              <h3>🛠️ Admin</h3>
              <ul class="menu-items">
                <li><a href="/admin" class="menu-item" data-route="/admin">${renderNavLabel('🛠️', 'Admin Settings')}</a></li>
              </ul>
            </div>

            <!-- Utilities -->
            <div class="menu-section">
              <h3>Settings</h3>
              <div class="menu-utilities">
                <theme-toggle></theme-toggle>
              </div>
            </div>

            <!-- Support Armadillo Mascot -->
            <div class="menu-section support-mascot-section">
              <button class="support-mascot-btn" id="supportMascotBtn" title="Get Help & Support">
                ${renderSafetyArmadillo()}
              </button>
            </div>
          </div>
        </nav>

        <!-- Overlay for mobile -->
        <div class="menu-overlay" id="menuOverlay"></div>
      </div>
    `;

    // Update admin visibility after render if we have user info
    if (this.currentUser) {
      this.updateAdminVisibility();
    }
    this.updateFeatureNavVisibility();
  }

  attachEventListeners() {
    const menuOverlay = this.querySelector('#menuOverlay');
    const closeMenuBtn = this.querySelector('#closeMenuBtn');
    const supportMascotBtn = this.querySelector('#supportMascotBtn');

    // Listen for mobile taco button click from header
    const mobileTacoBtn = document.querySelector('#mobileTacoBtn');
    if (mobileTacoBtn) {
      mobileTacoBtn.addEventListener('click', () => {
        this.toggleMenu();
      });
    }

    // Close button click
    closeMenuBtn.addEventListener('click', () => {
      this.closeMenu();
    });

    // Overlay click
    menuOverlay.addEventListener('click', () => {
      this.closeMenu();
    });

    // Support mascot click - opens support interface
    if (supportMascotBtn) {
      supportMascotBtn.addEventListener('click', () => {
        this.openSupportInterface();
      });
    }

    // Menu item clicks
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-context-pop-top]')) {
        e.preventDefault();
        popTopLayer();
        return;
      }
      const menuItem = e.target.closest('.menu-item');
      if (menuItem) {
        // Close menu on mobile after navigation
        if (!this.isWideScreen) {
          setTimeout(() => this.closeMenu(), 100);
        }
      }
    });

    // Listen for route changes to update active item
    window.addEventListener('popstate', () => {
      this.updateActiveMenuItem();
    });

    document.addEventListener('section-shown', () => {
      this.updateActiveMenuItem();
    });
  }

  setupResponsiveHandling() {
    let resizeTimeout;
    const handleResize = () => {
      // Debounce resize events to avoid excessive reflows
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Use requestAnimationFrame to batch DOM reads and writes
        requestAnimationFrame(() => {
          const wasWideScreen = this.isWideScreen;
          // Read layout property once
          const width = window.innerWidth;
          this.isWideScreen = width >= 1024;

          if (wasWideScreen !== this.isWideScreen) {
            // Batch all DOM queries
            const slideMenu = this.querySelector('#slideMenu');
            const menuOverlay = this.querySelector('#menuOverlay');
            const mobileTacoBtn = document.querySelector('#mobileTacoBtn');

            // Batch all DOM writes in a single frame
            requestAnimationFrame(() => {
              if (this.isWideScreen) {
                // Switch to always-open mode
                slideMenu.classList.add('always-open');
                slideMenu.classList.remove('open');
                menuOverlay.classList.remove('visible');
                // Hide mobile button on wide screens
                if (mobileTacoBtn) {
                  mobileTacoBtn.style.display = 'none';
                }
                this.isOpen = false;
              } else {
                // Switch to mobile mode
                slideMenu.classList.remove('always-open');
                // Show mobile button on small screens (if authenticated)
                if (mobileTacoBtn && this.currentUser) {
                  mobileTacoBtn.style.display = 'flex';
                }
                if (this.isOpen) {
                  slideMenu.classList.add('open');
                  menuOverlay.classList.add('visible');
                }
              }
            });
          }
        });
      }, 100); // Debounce by 100ms
    };

    window.addEventListener('resize', handleResize);
    // Defer initial check to avoid forced reflow on component mount
    requestAnimationFrame(() => {
      handleResize();
    });
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user) {
      this.updateAdminVisibility();
      this.updateFeatureNavVisibility();
      this.showMenu();
      this.loadFriendRequestCount();
      return;
    }
    this.hideMenu();
  }


  showMenu() {
    this.style.display = 'block';
    // Only show mobile taco button on small screens
    const mobileTacoBtn = document.querySelector('#mobileTacoBtn');
    if (mobileTacoBtn && !this.isWideScreen) {
      mobileTacoBtn.style.display = 'flex';
    }
  }

  hideMenu() {
    clearContextNav();
    this.style.display = 'none';
    // Also hide mobile taco button
    const mobileTacoBtn = document.querySelector('#mobileTacoBtn');
    if (mobileTacoBtn) {
      mobileTacoBtn.style.display = 'none';
    }
    // Close menu if it's open
    this.closeMenu();
  }

  updateAdminVisibility() {
    const adminSection = this.querySelector('#adminSection');
    if (adminSection) {
      if (this.currentUser && this.currentUser.role === 'admin') {
        adminSection.style.display = 'block';
      } else {
        adminSection.style.display = 'none';
      }
    }
  }

  updateFeatureNavVisibility() {
    const paradeItem = this.querySelector('#mascotParadeNavItem');
    if (paradeItem) {
      paradeItem.style.display = isFeatureEnabled('mascot_parade') ? '' : 'none';
    }
  }

  toggleMenu() {
    if (this.isWideScreen) return; // No toggle on wide screens

    const mobileTacoBtn = document.querySelector('#mobileTacoBtn');
    const slideMenu = this.querySelector('#slideMenu');
    const menuOverlay = this.querySelector('#menuOverlay');

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      slideMenu.classList.add('open');
      menuOverlay.classList.add('visible');
      if (mobileTacoBtn) mobileTacoBtn.classList.add('menu-open');
    } else {
      slideMenu.classList.remove('open');
      menuOverlay.classList.remove('visible');
      if (mobileTacoBtn) mobileTacoBtn.classList.remove('menu-open');
    }
  }

  closeMenu() {
    if (this.isWideScreen) return; // No close on wide screens

    const mobileTacoBtn = document.querySelector('#mobileTacoBtn');
    const slideMenu = this.querySelector('#slideMenu');
    const menuOverlay = this.querySelector('#menuOverlay');

    this.isOpen = false;
    slideMenu.classList.remove('open');
    menuOverlay.classList.remove('visible');
    if (mobileTacoBtn) mobileTacoBtn.classList.remove('menu-open');
  }

  escapeNavHtml(str) {
    if (str === null || str === undefined) {
      return '';
    }
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  syncContextNavDom() {
    const mount = this.querySelector('#contextNavMount');
    const primary = this.querySelector('#primarySiteNav');
    if (!mount || !primary) {
      return;
    }

    const stack = getContextNavLayers();
    if (stack.length === 0) {
      mount.innerHTML = '';
      mount.hidden = true;
      primary.hidden = false;
      return;
    }

    mount.hidden = false;
    primary.hidden = true;

    const blocks = stack.map((layer, index) => {
      const isTop = index === stack.length - 1;
      const title = this.escapeNavHtml(layer.title);
      const subtitle = layer.subtitle
        ? `<p class="context-nav-subtitle">${this.escapeNavHtml(layer.subtitle)}</p>`
        : '';
      const backBtn = isTop
        ? '<button type="button" class="context-nav-back-btn" data-context-pop-top>← Back</button>'
        : '';
      const itemsHtml = (layer.items || []).map((it) => {
        const label = this.escapeNavHtml(it.label);
        if (it.kind === 'heading') {
          return `<li class="context-nav-heading-item"><span class="context-nav-heading">${label}</span></li>`;
        }
        const href = this.escapeNavHtml(it.href || '#');
        const icon = it.icon ? renderEmojiBadge(it.icon, 'sm') : '';
        const dr = it.dataRoute || it.href || '';
        const dataRoute = this.escapeNavHtml(dr);
        const badge = it.badge
          ? `<span class="notification-badge context-nav-badge">${this.escapeNavHtml(it.badge)}</span>`
          : '';
        const indentClass = it.indent ? ' context-nav-item-indented' : '';
        return `<li><a href="${href}" class="menu-item context-nav-item${indentClass}" data-route="${dataRoute}">${icon}${label}${badge}</a></li>`;
      }).join('');

      const titleBlock = title
        ? `<h3 class="context-nav-title">${title}</h3>`
        : '';

      return `
        <div class="menu-section context-nav-layer" data-context-owner="${this.escapeNavHtml(layer.ownerId)}" data-context-id="${this.escapeNavHtml(layer.id)}">
          <div class="context-nav-layer-head">
            ${titleBlock}
            ${backBtn}
          </div>
          ${subtitle}
          <ul class="menu-items">${itemsHtml}</ul>
        </div>
      `;
    }).join('');

    mount.innerHTML = blocks;
    this.updateActiveMenuItem();
  }

  updateDocsLink() {
    const docsLink = this.querySelector('#primarySiteNav a[data-route="/docs"], #primarySiteNav a[href^="/docs"]');
    if (!docsLink) {
      return;
    }

    const currentPath = window.location.pathname;
    if (currentPath === '/docs' || currentPath.startsWith('/docs/')) {
      docsLink.href = '/docs';
      docsLink.dataset.route = '/docs';
      return;
    }

    const href = appendReturnTo('/docs');
    docsLink.href = href;
    docsLink.dataset.route = href;
  }

  updateActiveMenuItem() {
    const currentPath = window.location.pathname;
    this.updateDocsLink();

    this.querySelectorAll('#primarySiteNav .menu-item').forEach(item => {
      const route = item.dataset.route;
      if (route === currentPath ||
          (route === '/' && currentPath === '/') ||
          (route === '/groups' && (currentPath === '/groups' || currentPath.startsWith('/groups/'))) ||
          (route === '/memories' && currentPath.startsWith('/memories')) ||
          (route === '/settings' && currentPath.startsWith('/settings'))) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    const contextLinks = [...this.querySelectorAll('a.context-nav-item')];
    contextLinks.forEach(a => a.classList.remove('active'));
    if (contextLinks.length > 0) {
      const matching = contextLinks.filter(a => {
        const r = a.dataset.route;
        return r && (currentPath === r || currentPath.startsWith(`${r}/`));
      });
      if (matching.length > 0) {
        matching.sort((a, b) => (b.dataset.route || '').length - (a.dataset.route || '').length);
        matching[0].classList.add('active');
      }
    }
  }

  async loadFriendRequestCount() {
    if (!this.currentUser || !window.API) {
      return;
    }

    try {
      const response = await window.API.request('/friends');
      const counts = response.counts || {};
      const receivedRequests = counts.receivedRequests || 0;
      const pendingInvitations = counts.pendingInvitations || 0;

      const totalCount = receivedRequests + pendingInvitations;
      this.updateNotificationBadge(totalCount);
    } catch (error) {
      window.logger.error('Failed to load friend request count:', error);
      this.updateNotificationBadge(0);
    }
  }

  updateNotificationBadge(count) {
    const badge = this.querySelector('#friendsBadge');
    if (!badge) return;

    if (count > 0) {
      badge.textContent = count;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }

  openSupportInterface() {
    // TODO: Open support interface when <safety-armadillo> component is implemented
    // For now, we'll dispatch an event that can be handled by the support component
    // or navigate to a support route when it's created
    const event = new CustomEvent('open-support', {
      bubbles: true,
      detail: { source: 'taco-menu' }
    });
    this.dispatchEvent(event);

    // Close menu on mobile after opening support
    if (!this.isWideScreen) {
      this.closeMenu();
    }
  }

}

// Register the custom element
customElements.define('taco-menu', TacoMenu);
